import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import {
  Grid,
  TextField,
  MenuItem,
  Alert,
  Box,
  Typography,
  Autocomplete,
  Divider,
  CircularProgress,
} from "@mui/material";
import { FormDialog } from "../../../components/common/FormDialog/FormDialog";
import { Column, DataTable } from "../../../components/common/DataTable/DataTable";
import * as Yup from "yup";
import { 
  useCreatePreApprovedAbsenceMutation,
  usePreviewAbsenceRequestMutation,
} from "../../../api/absenceRequestsApi";
import { useGetEmployeesQuery } from "../../../api/employeesApi";
import {
  AbsenceCategory,
  AbsenceCategoryNames,
  getAbsenceCategoryColor,
} from "../../../constants/absenceEnums";
import { calculateBusinessDays, calculateTotalDays } from "../../../utils/absenceUtils";
import { extractAllErrors } from "../../../utils/errorUtils";
import { debounce } from "../../../utils/debounce";
import { AbsenceRequestPreviewDto } from "../../../types/absence";
import { DateRangeDisplay } from "../../../components/absence";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WarningIcon from "@mui/icons-material/Warning";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import InfoIcon from "@mui/icons-material/Info";
import WeekendIcon from "@mui/icons-material/Weekend";
import { Chip, Tooltip } from "@mui/material";

interface CreatePreApprovedAbsenceDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormValues {
  employeeId: number | null;
  absenceCategory: AbsenceCategory | null;
  startDate: string | null;
  endDate: string | null;
  requestReason: string;
}

const validationSchema = Yup.object({
  employeeId: Yup.number()
    .required("Employee is required")
    .min(1, "Employee is required"),
  absenceCategory: Yup.number()
    .oneOf(
      [
        AbsenceCategory.Vacation,
        AbsenceCategory.SickDays,
        AbsenceCategory.MedicalLeave,
        AbsenceCategory.Maternity,
        AbsenceCategory.Parental,
        AbsenceCategory.FamilyHoliday,
        AbsenceCategory.EarlyLeave,
        AbsenceCategory.LateOnShift,
      ],
      "Invalid absence category"
    )
    .required("Absence category is required"),
  startDate: Yup.date().required("Start date is required"),
  endDate: Yup.date()
    .required("End date is required")
    .test(
      "is-after-start",
      "End date must be on or after start date",
      function (value) {
        const { startDate } = this.parent;
        if (!startDate || !value) return true;
        return new Date(value) >= new Date(startDate);
      }
    ),
  requestReason: Yup.string()
    .required("Reason is required")
    .max(500, "Reason cannot exceed 500 characters"),
});

export const CreatePreApprovedAbsenceDialog: React.FC<
  CreatePreApprovedAbsenceDialogProps
> = ({ open, onClose, onSuccess }) => {
  const [createPreApprovedAbsence, { isLoading: isMutationLoading, error, reset }] =
    useCreatePreApprovedAbsenceMutation();
  
  const [previewAbsenceRequest, { isLoading: isPreviewLoading }] = 
    usePreviewAbsenceRequestMutation();
  
  const { data: employees = [], isLoading: isLoadingEmployees } = useGetEmployeesQuery();

  // Track validation warnings and bypass state
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
  const [showBypassOption, setShowBypassOption] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Preview state
  const [previewData, setPreviewData] = useState<AbsenceRequestPreviewDto | null>(null);
  
  // Reset validation state when modal opens
  useEffect(() => {
    if (open) {
      setValidationWarnings([]);
      setShowBypassOption(false);
      setIsSubmitting(false);
      setPreviewData(null);
      reset();
    }
  }, [open, reset]);

  const formik = useFormik<FormValues>({
    initialValues: {
      employeeId: null,
      absenceCategory: null,
      startDate: null,
      endDate: null,
      requestReason: "",
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        await createPreApprovedAbsence({
          employeeId: values.employeeId!,
          absenceCategory: values.absenceCategory!,
          startDate: values.startDate!,
          endDate: values.endDate!,
          requestReason: values.requestReason,
          notes: undefined,
          bypassValidation: showBypassOption, // Set bypass flag on second attempt
        }).unwrap();

        // Reset form, warnings, and close
        formik.resetForm();
        setValidationWarnings([]);
        setShowBypassOption(false);
        setIsSubmitting(false);
        onClose();
        onSuccess?.();
      } catch (err: any) {
        setIsSubmitting(false);
        // On first submission attempt, check if we got validation errors
        if (!showBypassOption && err?.data?.errors) {
          const errors = extractAllErrors(err);
          
          if (errors.length > 0) {
            // Messages are already context-aware from backend (isAdminCreating: true)
            setValidationWarnings(errors);
            setShowBypassOption(true);
            return; // Don't close dialog, let user decide
          }
        }
        
        // For second attempt errors or other errors, just show error normally
        console.error("Failed to create pre-approved absence:", err);
      }
    },
  });

  const businessDays = useMemo(() => {
    if (formik.values.startDate && formik.values.endDate) {
      return calculateBusinessDays(formik.values.startDate, formik.values.endDate);
    }
    return 0;
  }, [formik.values.startDate, formik.values.endDate]);

  const totalDays = useMemo(() => {
    if (formik.values.startDate && formik.values.endDate) {
      return calculateTotalDays(formik.values.startDate, formik.values.endDate);
    }
    return 0;
  }, [formik.values.startDate, formik.values.endDate]);

  const handleClose = () => {
    formik.resetForm();
    setValidationWarnings([]);
    setShowBypassOption(false);
    setIsSubmitting(false);
    onClose();
  };

  // Get available absence categories
  const availableCategories = useMemo(() => {
    return [
      AbsenceCategory.Vacation,
      AbsenceCategory.SickDays,
      AbsenceCategory.MedicalLeave,
      AbsenceCategory.Maternity,
      AbsenceCategory.Parental,
      AbsenceCategory.FamilyHoliday,
      AbsenceCategory.EarlyLeave,
      AbsenceCategory.LateOnShift,
    ];
  }, []);

  // Find selected employee for display
  const selectedEmployee = useMemo(() => {
    return employees.find((emp) => emp.id === formik.values.employeeId);
  }, [employees, formik.values.employeeId]);

  const worksWeekends = selectedEmployee?.worksWeekends ?? false;
  const daysToDeduct = worksWeekends ? totalDays : businessDays;

  // Debounced preview function
  const debouncedPreview = useCallback(
    debounce(async (
      employeeId: number,
      absenceCategory: AbsenceCategory,
      startDate: string,
      endDate: string
    ) => {
      try {
        const result = await previewAbsenceRequest({
          employeeId,
          absenceCategory,
          startDate,
          endDate,
        }).unwrap();
        setPreviewData(result);
      } catch (error) {
        console.error("Preview failed:", error);
        setPreviewData(null);
      }
    }, 500),
    [previewAbsenceRequest]
  );

  // Trigger preview when form values change
  useEffect(() => {
    if (
      formik.values.employeeId &&
      formik.values.absenceCategory !== null &&
      formik.values.startDate &&
      formik.values.endDate &&
      !formik.errors.startDate &&
      !formik.errors.endDate
    ) {
      debouncedPreview(
        formik.values.employeeId,
        formik.values.absenceCategory,
        formik.values.startDate,
        formik.values.endDate
      );
    } else {
      setPreviewData(null);
    }
  }, [
    formik.values.employeeId,
    formik.values.absenceCategory,
    formik.values.startDate,
    formik.values.endDate,
    formik.errors.startDate,
    formik.errors.endDate,
    debouncedPreview,
  ]);

  const overlappingAbsenceColumns: Column[] = [
    {
      field: "employeeName",
      headerName: "Employee",
      renderCell: (value) => value ?? "-",
    },
    {
      field: "absenceCategoryName",
      headerName: "Type",
    },
    {
      field: "startDate",
      headerName: "Dates",
      renderCell: (_value, row) => (
        <DateRangeDisplay
          startDate={row.startDate}
          endDate={row.endDate}
          days={row.daysDeducted}
          variant="inline"
          showIcon={false}
        />
      ),
    },
    {
      field: "statusName",
      headerName: "Status",
    },
  ];

  return (
    <FormDialog
      open={open}
      title="Create Pre-Approved Absence"
      onClose={handleClose}
      onSubmit={formik.handleSubmit}
      isLoading={isSubmitting || formik.isSubmitting}
      submitLabel={showBypassOption ? "Create Anyway" : "Create Absence"}
      maxWidth="sm"
    >
      <Grid container spacing={3}>
        {/* Warning Alert for bypassable validation errors */}
        {showBypassOption && validationWarnings.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning" icon={<WarningIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                The following validation rules would normally block this absence:
              </Typography>
              <Box>
                {validationWarnings.map((warning, index) => (
                  <Box key={index} sx={{ mb: index < validationWarnings.length - 1 ? 0.5 : 0 }}>
                    • {warning}
                  </Box>
                ))}
              </Box>
              <Typography variant="body2" sx={{ mt: 2, fontStyle: "italic" }}>
                Click "Create Anyway" to bypass these rules and create the absence.
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Error Alert for hard errors */}
        {error && !showBypassOption && (
          <Grid item xs={12}>
            <Alert severity="error">
              <Box>
                {extractAllErrors(error).map((err, index) => (
                  <Box key={index} sx={{ mb: index < extractAllErrors(error).length - 1 ? 1 : 0 }}>
                    • {err}
                  </Box>
                ))}
              </Box>
            </Alert>
          </Grid>
        )}

        {/* Employee Selection */}
        <Grid item xs={12}>
          <Autocomplete
            options={employees}
            getOptionLabel={(option) => 
              `${option.firstName} ${option.lastName}`
            }
            value={selectedEmployee || null}
            onChange={(_, newValue) => {
              formik.setFieldValue("employeeId", newValue?.id || null);
            }}
            onBlur={formik.handleBlur}
            loading={isLoadingEmployees}
            disabled={isLoadingEmployees}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employee"
                name="employeeId"
                error={formik.touched.employeeId && Boolean(formik.errors.employeeId)}
                helperText={formik.touched.employeeId && formik.errors.employeeId}
                required
                size="small"
              />
            )}
          />
        </Grid>

        {/* Works Weekends indicator for selected employee */}
        {selectedEmployee && (
          <Grid item xs={12}>
            <Tooltip
              title={
                worksWeekends
                  ? "This employee works weekends — Saturday and Sunday count toward balance deduction."
                  : "This employee does not work weekends — only business days (Mon–Fri) will be deducted."
              }
              placement="top"
            >
              <Chip
                icon={<WeekendIcon />}
                label={
                  worksWeekends
                    ? `${selectedEmployee.firstName} works weekends — all days count`
                    : `${selectedEmployee.firstName} doesn't work weekends — business days only`
                }
                size="small"
                sx={{
                  backgroundColor: worksWeekends ? "#FF8F00" : "grey.300",
                  color: worksWeekends ? "#fff" : "text.secondary",
                  fontWeight: 500,
                  "& .MuiChip-icon": { color: worksWeekends ? "#fff" : "text.secondary" },
                }}
              />
            </Tooltip>
          </Grid>
        )}

        {/* Absence Category */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            label="Absence Type"
            name="absenceCategory"
            value={formik.values.absenceCategory || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.absenceCategory &&
              Boolean(formik.errors.absenceCategory)
            }
            helperText={
              formik.touched.absenceCategory && formik.errors.absenceCategory
            }
            required
            size="small"
          >
            {availableCategories.map((category) => (
              <MenuItem key={category} value={category}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: getAbsenceCategoryColor(category),
                    }}
                  />
                  {AbsenceCategoryNames[category]}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Date Range */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="Start Date"
            name="startDate"
            value={formik.values.startDate ? formik.values.startDate.split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value;
              formik.setFieldValue('startDate', value || null);
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.startDate && Boolean(formik.errors.startDate)}
            helperText={formik.touched.startDate && formik.errors.startDate}
            required
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="End Date"
            name="endDate"
            value={formik.values.endDate ? formik.values.endDate.split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value;
              formik.setFieldValue('endDate', value || null);
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.endDate && Boolean(formik.errors.endDate)}
            helperText={formik.touched.endDate && formik.errors.endDate}
            required
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        {/* Days Display */}
        {formik.values.startDate && formik.values.endDate && daysToDeduct > 0 && (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 2,
                backgroundColor: "primary.light",
                borderRadius: 1,
                color: "primary.contrastText",
              }}
            >
              <CalendarTodayIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {daysToDeduct} day{daysToDeduct !== 1 ? "s" : ""} requested
                {worksWeekends && totalDays !== businessDays && (
                  <span style={{ opacity: 0.8, marginLeft: 6, fontSize: "0.85em" }}>
                    (including weekends)
                  </span>
                )}
                {!worksWeekends && (
                  <span style={{ opacity: 0.8, marginLeft: 6, fontSize: "0.85em" }}>
                    (business days only)
                  </span>
                )}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Reason */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason"
            name="requestReason"
            value={formik.values.requestReason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.requestReason &&
              Boolean(formik.errors.requestReason)
            }
            helperText={
              formik.touched.requestReason && formik.errors.requestReason
            }
            required
            size="small"
            placeholder="Provide a reason for this absence..."
          />
        </Grid>

        {/* Preview Section */}
        {(isPreviewLoading || previewData) && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Loading State */}
            {isPreviewLoading && (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading preview...
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Preview Data */}
            {!isPreviewLoading && previewData && (
              <>
                {/* Balance Information */}
                {previewData.balanceInfo && (
                  <>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <AccountBalanceIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                          Balance Information
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 1, 
                        backgroundColor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider"
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                          Current Balance
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="h6" color="primary.main" fontWeight={700}>
                            {previewData.balanceInfo.currentBalance.toFixed(1)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            days
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 1, 
                        backgroundColor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider"
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                          Requested Days
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="h6" color="warning.main" fontWeight={700}>
                            {previewData.balanceInfo.requestedDays.toFixed(1)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            days
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 1, 
                        backgroundColor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider"
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                          After Approval
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography 
                            variant="h6" 
                            color={previewData.balanceInfo.projectedBalance >= 0 ? "success.main" : "error.main"} 
                            fontWeight={700}
                          >
                            {previewData.balanceInfo.projectedBalance.toFixed(1)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            days
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {previewData.balanceInfo.warningMessage && (
                      <Grid item xs={12}>
                        <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            {previewData.balanceInfo.warningMessage}
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                  </>
                )}

                {/* Overlapping Absences */}
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, mt: 2 }}>
                    <InfoIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                      Overlapping Absences
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  {previewData.overlappingAbsences.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No overlapping absences found.
                    </Typography>
                  ) : (
                    <DataTable
                      items={previewData.overlappingAbsences}
                      columns={overlappingAbsenceColumns}
                      totalCount={previewData.overlappingAbsences.length}
                    />
                  )}
                </Grid>
              </>
            )}
          </>
        )}

      </Grid>
    </FormDialog>
  );
};

